/**
 * <bh-image-upload> — reference-photo picker for the cake configurator.
 *
 * Wraps a plain <input type="file"> so the file still travels as a normal line-item property
 * (`properties[Reference Image]`) on a multipart form post — no upload service, no storage of
 * our own. This element only adds what the bare input lacks: a real drop target, a thumbnail
 * so people can see they attached the right photo, and client-side validation with a readable
 * message instead of a checkout that fails later.
 *
 * Markup contract (see snippets/cake-block-image-upload.liquid):
 *   <bh-image-upload data-max-mb="10" data-err-type="…" data-err-size="…">
 *     <input type="file" accept="image/*" …>
 *     <label for="…" data-bh-drop>…</label>
 *     <div data-bh-preview hidden><img data-bh-thumb><span data-bh-name></span>
 *       <span data-bh-size></span><button data-bh-clear></button></div>
 *     <p data-bh-error role="alert" hidden></p>
 *   </bh-image-upload>
 *
 * ponytail: no cropping, rotation or client-side compression. Phone photos are already
 *   acceptable; add compression only if 10 MB uploads turn out to be a real drop-off point.
 */
class BhImageUpload extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="file"]');
    if (!this.input) return;
    this.drop = this.querySelector('[data-bh-drop]');
    this.preview = this.querySelector('[data-bh-preview]');
    this.thumb = this.querySelector('[data-bh-thumb]');
    this.nameEl = this.querySelector('[data-bh-name]');
    this.sizeEl = this.querySelector('[data-bh-size]');
    this.errorEl = this.querySelector('[data-bh-error]');
    this.maxBytes = (parseFloat(this.dataset.maxMb) || 10) * 1024 * 1024;
    this.objectUrl = null;

    this.input.addEventListener('change', () => this._onPick());
    this.querySelector('[data-bh-clear]')?.addEventListener('click', () => this._clear(true));

    // Drag & drop onto the label. Assigning to input.files keeps the file inside the form,
    // so the existing multipart submit carries it with no extra plumbing.
    if (this.drop) {
      ['dragenter', 'dragover'].forEach((ev) =>
        this.drop.addEventListener(ev, (e) => { e.preventDefault(); this.drop.classList.add('is-dragover'); }));
      ['dragleave', 'drop'].forEach((ev) =>
        this.drop.addEventListener(ev, () => this.drop.classList.remove('is-dragover')));
      this.drop.addEventListener('drop', (e) => {
        e.preventDefault();
        if (!e.dataTransfer?.files?.length) return;
        const dt = new DataTransfer();
        dt.items.add(e.dataTransfer.files[0]);
        this.input.files = dt.files;
        this._onPick();
      });
    }
  }

  _onPick() {
    const file = this.input.files && this.input.files[0];
    this._showError('');
    if (!file) return this._clear(false);

    if (!/^image\//.test(file.type)) return this._reject(this.dataset.errType);
    if (file.size > this.maxBytes) return this._reject(this.dataset.errSize);

    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    if (this.thumb) { this.thumb.src = this.objectUrl; this.thumb.alt = file.name; }
    if (this.nameEl) this.nameEl.textContent = file.name;
    if (this.sizeEl) this.sizeEl.textContent = `${(file.size / 1024 / 1024).toFixed(1)} MB`;
    if (this.preview) this.preview.hidden = false;
    if (this.drop) this.drop.hidden = true;
  }

  _reject(message) {
    this.input.value = '';
    this._clear(false);
    this._showError(message || 'Invalid file');
  }

  _clear(focusBack) {
    this.input.value = '';
    if (this.objectUrl) { URL.revokeObjectURL(this.objectUrl); this.objectUrl = null; }
    if (this.thumb) this.thumb.removeAttribute('src');
    if (this.preview) this.preview.hidden = true;
    if (this.drop) this.drop.hidden = false;
    this._showError('');
    if (focusBack && this.drop) this.drop.focus?.();
  }

  _showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }
}

if (!customElements.get('bh-image-upload')) {
  customElements.define('bh-image-upload', BhImageUpload);
}
